"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type RecoveryDisplayPreviewProps = {
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

/*
 * Contrast therapy, split down the middle: an ice-blue plunge room on the
 * left (a round tub of glowing cold water with floating ice and frost vapour)
 * blending into a cedar sauna on the right (a stove of glowing stones
 * throwing steam, a bucket and ladle, a rolled towel on the bench). The two
 * temperatures meet behind the participant pill.
 *
 * Motion: vapour drifts off the plunge and steam rises off the stones — CSS
 * transform and opacity only, off under reduced motion.
 */
const RECOVERY_KEYFRAMES = `
@keyframes rec-steam {
  0% { transform: translate3d(0, 6px, 0) scale(0.7); opacity: 0 }
  30% { opacity: 0.8 }
  100% { transform: translate3d(-6px, -40px, 0) scale(1.5); opacity: 0 }
}
@keyframes rec-vapor {
  0%, 100% { transform: translate3d(-3px, 0, 0); opacity: 0.55 }
  50% { transform: translate3d(4px, -4px, 0); opacity: 0.9 }
}
.rec-steam { animation: rec-steam 4.2s ease-out infinite; transform-box: fill-box; transform-origin: center }
.rec-vapor { animation: rec-vapor 6s ease-in-out infinite; transform-box: fill-box; transform-origin: center }
@media (prefers-reduced-motion: reduce) {
  .rec-steam { animation: none; opacity: 0.45 }
  .rec-vapor { animation: none }
}
`;

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `rec-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <radialGradient id={id("water")} cx="0.45" cy="0.4" r="0.6">
          <stop offset="0" stopColor="#d9fbff" />
          <stop offset="0.45" stopColor="#5fd0f2" />
          <stop offset="1" stopColor="#0d6aa0" />
        </radialGradient>
        <linearGradient id={id("tub")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#0b1219" />
          <stop offset="0.3" stopColor="#2b3a48" />
          <stop offset="0.55" stopColor="#141d26" />
          <stop offset="1" stopColor="#070b10" />
        </linearGradient>
        <linearGradient id={id("ice")} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#a9e6ff" />
        </linearGradient>
        <radialGradient id={id("puff")}>
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={id("vapor")}>
          <stop offset="0" stopColor="#dff7ff" stopOpacity="0.7" />
          <stop offset="1" stopColor="#dff7ff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={id("stove")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#5b5f68" />
          <stop offset="0.4" stopColor="#2a2c32" />
          <stop offset="1" stopColor="#111215" />
        </linearGradient>
        <radialGradient id={id("ember")}>
          <stop offset="0" stopColor="#ffd27a" />
          <stop offset="0.5" stopColor="#ff7a1a" />
          <stop offset="1" stopColor="#ff7a1a" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={id("cedar")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#d99a5b" />
          <stop offset="1" stopColor="#8a5226" />
        </linearGradient>
      </defs>
    </svg>
  );
}

const ICE = [
  { x: 46, y: 108, r: 14 },
  { x: 64, y: 113, r: -10 },
  { x: 84, y: 107, r: 24 },
  { x: 100, y: 112, r: -18 },
  { x: 56, y: 103, r: 40 },
];

const STONES = [
  { x: 204, y: 104, rx: 7, ry: 5, c: "#7b726b" },
  { x: 216, y: 100, rx: 8, ry: 5.5, c: "#5f5853" },
  { x: 229, y: 102, rx: 7.5, ry: 5, c: "#8a8079" },
  { x: 242, y: 104, rx: 7, ry: 5, c: "#6a625c" },
  { x: 211, y: 95, rx: 6.5, ry: 4.5, c: "#958b83" },
  { x: 224, y: 93, rx: 7, ry: 5, c: "#6f6761" },
  { x: 236, y: 96, rx: 6, ry: 4.2, c: "#857c75" },
  { x: 219, y: 87, rx: 6, ry: 4, c: "#7c736d" },
  { x: 231, y: 88, rx: 5.5, ry: 4, c: "#9a9089" },
];

function Rooms({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute top-0 left-1/2 -translate-x-1/2"
      width="300"
      height="220"
      viewBox="0 0 300 220"
      aria-hidden="true"
      focusable="false"
    >
      {/* ── Cold side ───────────────────────────── */}
      {/* Cool LED cove */}
      <rect
        x="0"
        y="40"
        width="118"
        height="2"
        rx="1"
        fill="#9fe9ff"
        opacity="0.9"
      />
      <rect
        x="0"
        y="42"
        width="118"
        height="26"
        fill="#9fe9ff"
        opacity="0.08"
      />
      {/* Frost sparkles */}
      {[
        [22, 60],
        [70, 54],
        [104, 74],
        [38, 82],
        [128, 58],
      ].map(([x, y]) => (
        <path
          key={`frost-${x}-${y}`}
          d={`M${x - 3} ${y} H${x + 3} M${x} ${y - 3} V${y + 3}`}
          stroke="#e6fbff"
          strokeOpacity="0.8"
          strokeWidth="0.8"
          strokeLinecap="round"
        />
      ))}
      {/* Plunge tub */}
      <ellipse cx="72" cy="170" rx="58" ry="8" fill="#02070b" opacity="0.55" />
      <path
        d="M18 110 L24 160 Q72 176 120 160 L126 110 Z"
        fill={`url(#${id("tub")})`}
      />
      <path
        d="M30 116 L34 158"
        stroke="#7fa2bd"
        strokeOpacity="0.35"
        strokeWidth="2"
      />
      <ellipse cx="72" cy="110" rx="54" ry="14" fill="#0c141c" />
      <ellipse
        cx="72"
        cy="111"
        rx="50"
        ry="11.5"
        fill={`url(#${id("water")})`}
      />
      <ellipse
        cx="72"
        cy="110"
        rx="54"
        ry="14"
        fill="none"
        stroke="#d6e6f2"
        strokeWidth="2.6"
      />
      <path
        d="M20 107 Q40 97 72 96.5"
        stroke="#ffffff"
        strokeOpacity="0.85"
        strokeWidth="1.2"
        fill="none"
      />
      {/* Water ripples */}
      <ellipse
        cx="74"
        cy="112"
        rx="22"
        ry="4.5"
        fill="none"
        stroke="#ffffff"
        strokeOpacity="0.35"
        strokeWidth="0.8"
      />
      <ellipse
        cx="74"
        cy="112"
        rx="34"
        ry="7.5"
        fill="none"
        stroke="#ffffff"
        strokeOpacity="0.18"
        strokeWidth="0.8"
      />
      {ICE.map((cube) => (
        <rect
          key={`ice-${cube.x}`}
          x={cube.x - 4.5}
          y={cube.y - 3.5}
          width="9"
          height="7"
          rx="1.6"
          transform={`rotate(${cube.r} ${cube.x} ${cube.y})`}
          fill={`url(#${id("ice")})`}
          stroke="#ffffff"
          strokeOpacity="0.8"
          strokeWidth="0.6"
        />
      ))}
      <text
        x="104"
        y="146"
        fill="#9fe9ff"
        fillOpacity="0.85"
        fontSize="8"
        fontWeight="800"
        letterSpacing="0.5"
      >
        3°
      </text>
      <ellipse
        className="rec-vapor"
        cx="66"
        cy="96"
        rx="40"
        ry="9"
        fill={`url(#${id("vapor")})`}
      />

      {/* ── Hot side ────────────────────────────── */}
      {/* Lamp */}
      <path d="M262 0 V22" stroke="#2a1a0e" strokeWidth="1.2" />
      <path d="M250 34 Q262 18 274 34 Z" fill="#7a4520" />
      <ellipse cx="262" cy="34" rx="12" ry="2.6" fill="#ffd08a" />
      <ellipse
        cx="262"
        cy="48"
        rx="38"
        ry="22"
        fill={`url(#${id("ember")})`}
        opacity="0.35"
      />
      {/* Bench + rolled towel */}
      <rect
        x="236"
        y="116"
        width="70"
        height="6"
        rx="1.5"
        fill={`url(#${id("cedar")})`}
      />
      <rect
        x="236"
        y="122"
        width="70"
        height="3"
        fill="#4a2a10"
        opacity="0.6"
      />
      <rect x="292" y="125" width="5" height="40" fill="#5c3415" />
      <rect x="262" y="104" width="26" height="12" rx="6" fill="#f4efe6" />
      <ellipse cx="288" cy="110" rx="3" ry="6" fill="#d9d0c2" />
      <path
        d="M288 106 Q285.5 110 288 114"
        stroke="#b9ad9c"
        strokeWidth="0.8"
        fill="none"
      />
      {/* Stove */}
      <ellipse cx="223" cy="168" rx="38" ry="6" fill="#120804" opacity="0.6" />
      <rect
        x="194"
        y="106"
        width="58"
        height="60"
        rx="3"
        fill={`url(#${id("stove")})`}
      />
      <rect x="198" y="114" width="50" height="40" rx="2" fill="#160d08" />
      <ellipse
        cx="223"
        cy="134"
        rx="28"
        ry="20"
        fill={`url(#${id("ember")})`}
      />
      {[202, 211, 220, 229, 238, 247].map((x) => (
        <rect
          key={`bar-${x}`}
          x={x - 1}
          y="113"
          width="2"
          height="42"
          fill="#3a3c42"
        />
      ))}
      <rect x="194" y="106" width="58" height="4" rx="2" fill="#6d717a" />
      <rect x="196" y="160" width="6" height="8" fill="#1a1b1f" />
      <rect x="244" y="160" width="6" height="8" fill="#1a1b1f" />
      <ellipse
        cx="223"
        cy="98"
        rx="30"
        ry="12"
        fill={`url(#${id("ember")})`}
        opacity="0.75"
      />
      {STONES.map((stone) => (
        <g key={`stone-${stone.x}-${stone.y}`}>
          <ellipse
            cx={stone.x}
            cy={stone.y}
            rx={stone.rx}
            ry={stone.ry}
            fill={stone.c}
          />
          <ellipse
            cx={stone.x - stone.rx * 0.3}
            cy={stone.y - stone.ry * 0.4}
            rx={stone.rx * 0.45}
            ry={stone.ry * 0.3}
            fill="#ffffff"
            opacity="0.22"
          />
          <path
            d={`M${stone.x - stone.rx} ${stone.y + 1} Q${stone.x} ${stone.y + stone.ry + 1.5} ${stone.x + stone.rx} ${stone.y + 1}`}
            stroke="#ff8a2a"
            strokeOpacity="0.7"
            strokeWidth="1"
            fill="none"
          />
        </g>
      ))}
      {/* Steam off the stones */}
      {[
        { cx: 214, cy: 78, r: 9, delay: "0s" },
        { cx: 229, cy: 74, r: 11, delay: "1.4s" },
        { cx: 222, cy: 80, r: 8, delay: "2.8s" },
      ].map((puff) => (
        <circle
          key={`steam-${puff.cx}`}
          className="rec-steam"
          cx={puff.cx}
          cy={puff.cy}
          r={puff.r}
          fill={`url(#${id("puff")})`}
          style={{ animationDelay: puff.delay }}
        />
      ))}
      {/* Bucket + ladle */}
      <path
        d="M276 128 L256 150"
        stroke="#9a6232"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M258 146 L282 146 L279 170 L261 170 Z"
        fill={`url(#${id("cedar")})`}
      />
      <ellipse cx="270" cy="146" rx="12" ry="3" fill="#4a2a10" />
      <path
        d="M259 154 H281 M260 164 H280"
        stroke="#c9ced6"
        strokeWidth="1.4"
      />
      <path
        d="M260 148 L262 168"
        stroke="#ffd7a8"
        strokeOpacity="0.5"
        strokeWidth="1"
      />
    </svg>
  );
}

export function RecoveryDisplayPreview({
  participants,
}: RecoveryDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Recovery"
      backgroundClassName="bg-[#123247]"
      participants={participants}
      scene={
        <>
          <style href="recovery-scene-keyframes" precedence="default">
            {RECOVERY_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          {/* Cold → hot */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(45% 70% at 22% 55%, rgba(130,225,255,0.4) 0%, transparent 70%), radial-gradient(45% 75% at 78% 58%, rgba(255,150,60,0.5) 0%, transparent 70%), linear-gradient(100deg, #0b2a44 0%, #144a70 30%, #2b4a5a 47%, #6e3b1c 55%, #a3561f 76%, #6a3210 100%)",
            }}
          />
          {/* Ice-room tiles, left */}
          <div
            className="absolute inset-y-0 left-0 w-[58%]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, transparent 0 17px, rgba(220,245,255,0.09) 17px 18px), repeating-linear-gradient(180deg, transparent 0 17px, rgba(220,245,255,0.09) 17px 18px)",
              maskImage: "linear-gradient(90deg, black 55%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(90deg, black 55%, transparent 100%)",
            }}
          />
          {/* Cedar slats, right */}
          <div
            className="absolute inset-y-0 right-0 w-[58%]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(180deg, rgba(255,214,160,0.08) 0 1px, transparent 1px 11px, rgba(40,16,4,0.35) 11px 12px)",
              maskImage: "linear-gradient(270deg, black 55%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(270deg, black 55%, transparent 100%)",
            }}
          />
          {/* Floor: wet stone → duckboard */}
          <div className="absolute inset-x-0 bottom-0 h-[24%] bg-[linear-gradient(90deg,#0a2032_0%,#0e2a3e_40%,#3a200d_60%,#2c1708_100%)]">
            <div className="absolute inset-x-0 top-0 h-px bg-white/15" />
            <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,transparent_0_22px,rgba(0,0,0,0.25)_22px_24px)]" />
          </div>

          <Rooms id={id} />
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_115%_120%_at_50%_45%,transparent_52%,rgba(0,0,0,0.5)_100%)]" />
      }
    />
  );
}
